import { usePermissao } from '@/utils/permissao';
import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useVoltar } from '@/utils/voltar';
import { SeguradoraService } from '../../services';
import SeguradoraTelefoneSection from './SeguradoraTelefoneSection';
import SeguradoraEmailSection from './SeguradoraEmailSection';
import { Seguradora } from '@/types/SeguradoraDTO';
import { toast } from 'sonner';
import { TelefoneContatoDTO } from '@/types/TelefoneContatoDTO';
import { EmailContatoDTO } from '@/types/EmailContatoDTO';
import { Row } from '@/components/grid/row';
import { Card } from '@/components/ui/card';
import { FormikInput } from '@/components/ui/input';
import { getTenant } from '@/utils/TenantUtils';

export const seguradoraFormValidationSchema = Yup.object().shape({
    nome: Yup.string().required('Nome é obrigatório'),
    telefones: Yup.array().of(
        Yup.object().shape({
          descricao: Yup.string().required("A descrição é obrigatória"),
          telefone: Yup.string().required("O telefone ou celular é obrigatório")
        })),
    emails: Yup.array().of(
        Yup.object().shape({
            descricao: Yup.string().required("A descrição é obrigatória"),
            email: Yup.string().required("O e-mail é obrigatório").email("O e-mail é inválido")
        }))
});

const initialValues: Seguradora = {
    nome: '',
    telefones: [],
    emails: [],
    ativo: true
};

type SeguradoraFormPageParams = {
    
}

const SeguradoraFormPage: React.FC<SeguradoraFormPageParams> = () => {
    const somenteLeitura = !usePermissao('SEGURADORA_WRITE');
    const [seguradora, setSeguradora] = useState<Seguradora>(initialValues);
    const [loading, setLoading] = useState<boolean>(false);
    const [indexTelefoneEditando, setIndexTelefoneEditando] = useState<number|undefined>(undefined);
    const [indexEmailEditando, setIndexEmailEditando] = useState<number|undefined>(undefined);
    const voltar = useVoltar();
    const {id} = useParams();

    useEffect(() => {
        if (id && id !== 'novo') {
            setLoading(true);
            
            SeguradoraService.getByCodigo(id)
              .then(result => {
                  setSeguradora(result.data);
              })
              .catch(error => {
                  console.error(error);
              })
              .finally(() => setLoading(false));
        }        
    },[id]);

    const onSave = (seguradoraDTO: Seguradora) => {
        setLoading(true);
        if (id && id !== 'novo'){
            SeguradoraService.update(seguradoraDTO, id)
            .then(result => {
                setSeguradora(result.data);
                toast.success('Seguradora alterada com sucesso.');
                voltar(`/${getTenant()}/seguradora`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        } else {
            SeguradoraService.create(seguradoraDTO)
            .then(result => {
                setSeguradora(result.data);
                toast.success('Seguradora cadastrada com sucesso.');
                voltar(`/${getTenant()}/seguradora`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        }        
    }

    const novoContatoTelefone = (formikProps: FormikProps<Seguradora>) => {
        var telefones = formikProps.values.telefones;
        var novoTelefone: TelefoneContatoDTO = {
            descricao: '',
            telefone: ''
        };
        telefones.push(novoTelefone);        
        formikProps.setFieldValue('telefones', telefones);
        setIndexTelefoneEditando(telefones.length-1);
    }

    const removerContatoTelefone = (formikProps: FormikProps<Seguradora>, indexRemover: number) => {
        var telefones = formikProps.values.telefones;
        telefones?.splice(indexRemover, 1);  
        formikProps.setFieldValue('telefones', telefones);
    }

    const novoContatoEmail = (formikProps: FormikProps<Seguradora>) => {
        var emails = formikProps.values.emails;
        var novo: EmailContatoDTO = {
            descricao: '',
            email: ''
        };
        emails.push(novo);        
        formikProps.setFieldValue('emails', emails);
        setIndexEmailEditando(emails.length-1);
    }

    const removerContatoEmail = (formikProps: FormikProps<Seguradora>, indexRemover: number) => {
        var emails = formikProps.values.emails;
        emails?.splice(indexRemover, 1);  
        formikProps.setFieldValue('emails', emails);
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="seguradora" />} title={somenteLeitura ? 'Seguradora' : id && id !== 'novo' ? 'Editar seguradora' : 'Nova seguradora'} />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={seguradora} validationSchema={seguradoraFormValidationSchema} onSubmit={onSave} enableReinitialize>
                    {(formikProps: FormikProps<Seguradora>) => (
                        <>
                        <Form>
                            <fieldset disabled={somenteLeitura} className="min-w-0">
                            <Row>
                                <FormikInput name='nome' label={'Nome'} size={10} />
                            </Row>
                            <Row>
                                <SeguradoraTelefoneSection 
                                    formikProps={formikProps} 
                                    loading={loading} 
                                    indexTelefoneEditando={indexTelefoneEditando}
                                    novoContatoTelefone={() => novoContatoTelefone(formikProps)} 
                                    alterarContatoTelefone={(index) => setIndexTelefoneEditando(index)}
                                    removerContatoTelefone={(index) => removerContatoTelefone(formikProps, index)}
                                    salvarContatoTelefone={() => setIndexTelefoneEditando(undefined)} />
                            </Row>
                            <Row>
                                <SeguradoraEmailSection 
                                    formikProps={formikProps} 
                                    loading={loading} 
                                    indexEditando={indexEmailEditando}
                                    novo={() => novoContatoEmail(formikProps)} 
                                    alterar={(index) => setIndexEmailEditando(index)}
                                    remover={(index) => removerContatoEmail(formikProps, index)}
                                    salvar={() => setIndexEmailEditando(undefined)} />
                            </Row>
                            </fieldset>
                            <FormActions loading={loading} onSave={formikProps.submitForm} somenteLeitura={somenteLeitura} />
                        </Form>
                        </>
                    )}                
                </Formik>
            </Card>       
        </div>
    );
}

export default SeguradoraFormPage;